const DAY_SECONDS = 24 * 60 * 60;

function randomItem(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function randomBetween(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function randomTime() {
  // Roughly 88% of traffic lands during the Polish business window.
  if (Math.random() < 0.88) return randomBetween(9 * 3600, 19 * 3600 - 1);
  const quietHours = [
    [0, 9 * 3600 - 1],
    [19 * 3600, DAY_SECONDS - 1],
  ];
  const window = Math.random() < 0.58 ? quietHours[0] : quietHours[1];
  return randomBetween(window[0], window[1]);
}

function pad(value) {
  return String(value).padStart(2, '0');
}

export function formatUtcTimestampFromWarsaw(date, seconds) {
  const safe = clamp(Math.floor(seconds), 0, DAY_SECONDS - 1);
  const [year, month, day] = date.split('-').map(Number);
  const hour = Math.floor(safe / 3600);
  const minute = Math.floor((safe % 3600) / 60);
  const second = safe % 60;
  const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });
  const parts = Object.fromEntries(formatter.formatToParts(new Date(desiredAsUtc)).map((part) => [part.type, part.value]));
  const represented = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second));
  const utcDate = new Date(desiredAsUtc - (represented - desiredAsUtc));
  return `${utcDate.getUTCFullYear()}-${pad(utcDate.getUTCMonth() + 1)}-${pad(utcDate.getUTCDate())} ${pad(utcDate.getUTCHours())}:${pad(utcDate.getUTCMinutes())}:${pad(utcDate.getUTCSeconds())}`;
}

export function formatPeriod(totalSeconds) {
  let value = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(value / 3600);
  value %= 3600;
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return [hours ? `${hours}ч.` : '', minutes ? `${minutes}м.` : '', seconds ? `${seconds}с.` : '']
    .filter(Boolean)
    .join(' ');
}

function chooseSales(sales, target, saleCount) {
  if (!sales.length || saleCount <= 0) return [];
  let best = [];
  let bestDifference = Infinity;

  // Randomized bounded search keeps offer selection varied while finding a close real-payout sum.
  for (let attempt = 0; attempt < 3500; attempt += 1) {
    const candidate = Array.from({ length: saleCount }, () => randomItem(sales));
    const sum = candidate.reduce((total, row) => total + Number(row.revenue || 0), 0);
    const difference = Math.abs(target - sum);
    if (difference < bestDifference || (difference === bestDifference && Math.random() < 0.5)) {
      best = candidate;
      bestDifference = difference;
    }
    if (bestDifference < 0.005) break;
  }
  return best;
}

function randomSaleCount(registrations, registrationsPerSale) {
  const ratio = Math.max(1, Number(registrationsPerSale) || 10);
  const probability = 1 / ratio;
  let sales = 0;
  for (let index = 0; index < registrations; index += 1) {
    if (Math.random() < probability) sales += 1;
  }
  return sales;
}

function syntheticId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function copyTemplate(template, overrides) {
  return {
    campaign: template.campaign || '',
    offer: template.offer || '',
    sub1: template.sub1 || '',
    sub2: template.sub2 || '',
    sub3: template.sub3 || '',
    sub4: template.sub4 || '',
    deviceModel: template.deviceModel || '',
    city: template.city || '',
    ...overrides,
  };
}

function randomSub3() {
  return randomItem(['AD1', 'AD2', 'ad3', 'ad4']);
}

function applyGeneratedFields(rows) {
  const iphoneModels = ['iPhone', 'iPhone 11', 'iPhone 12', 'iPhone 13', 'iPhone 14', 'iPhone 15', 'iPhone 16'];
  let nextDeviceAt = randomBetween(3, 6);

  rows.forEach((row, index) => {
    row.sub1 = 'klih';
    row.sub3 = randomSub3();
    row.sub4 = String(row.sub4 || '').toUpperCase();
    row.deviceModel = index === nextDeviceAt ? randomItem(iphoneModels) : '';
    if (index === nextDeviceAt) nextDeviceAt += randomBetween(4, 7);
  });

  // A sale is the continuation of its paired lead, so attribution must stay identical.
  rows.filter((row) => row.kind === 'sale').forEach((sale) => {
    const lead = rows.find((row) => row.kind === 'paired-lead' && row.pairId === sale.pairId);
    if (!lead) return;
    sale.campaign = lead.campaign;
    sale.offer = lead.offer;
    sale.sub1 = lead.sub1;
    sale.sub2 = lead.sub2;
    sale.sub3 = lead.sub3;
    sale.sub4 = lead.sub4;
    sale.deviceModel = lead.deviceModel;
    sale.city = lead.city;
  });
}

export function generateConversionPreview({ conversions, geos, targetRevenue, count, registrationsPerSale = 10, date }) {
  const selected = new Set(geos);
  const pool = conversions.filter((row) => (
    selected.has(String(row.sub4 || '').toUpperCase()) && String(row.campaign || '').trim()
  ));
  const leads = pool.filter((row) => row.origStatus === 'lead');
  const sales = pool.filter((row) => row.origStatus === 'sale' && Number(row.revenue) > 0);

  if (!pool.length || !leads.length) throw new Error('Для выбранных гео нет исторических лидов.');

  const registrationCount = clamp(Math.floor(Number(count) || 0), 1, 500);
  const target = Math.max(0, Number(targetRevenue) || 0);
  const saleCount = sales.length ? randomSaleCount(registrationCount, registrationsPerSale) : 0;
  const chosenSales = chooseSales(sales, target, saleCount);
  const rows = [];
  let pairNumber = 0;

  chosenSales.forEach((saleTemplate) => {
    pairNumber += 1;
    const geo = String(saleTemplate.sub4 || '').toUpperCase();
    const matchingLeads = leads.filter((lead) => (
      String(lead.sub4 || '').toUpperCase() === geo && lead.offer === saleTemplate.offer
    ));
    const leadTemplate = matchingLeads.length ? randomItem(matchingLeads) : saleTemplate;
    const leadSecond = Math.min(randomTime(), DAY_SECONDS - 121);
    const maxDelay = Math.max(120, Math.min(5 * 3600, DAY_SECONDS - 1 - leadSecond));
    const delay = randomBetween(Math.min(120, maxDelay), maxDelay);
    const saleSecond = leadSecond + delay;
    const pairId = `P${String(pairNumber).padStart(2, '0')}`;

    rows.push(copyTemplate(leadTemplate, {
      _seconds: leadSecond, pairId, kind: 'paired-lead', warsawDate: date, conversionTime: formatUtcTimestampFromWarsaw(date, leadSecond),
      period: '', status: 'Лид', origStatus: 'lead', revenue: 0,
    }));
    rows.push(copyTemplate(saleTemplate, {
      _seconds: saleSecond, pairId, kind: 'sale', warsawDate: date, conversionTime: formatUtcTimestampFromWarsaw(date, saleSecond),
      period: formatPeriod(delay), status: 'sale', origStatus: 'sale', revenue: Number(saleTemplate.revenue),
    }));
  });

  while (rows.filter((row) => row.origStatus === 'lead').length < registrationCount) {
    const template = randomItem(leads);
    const second = randomTime();
    rows.push(copyTemplate(template, {
      _seconds: second, pairId: '', kind: 'independent-lead', warsawDate: date, conversionTime: formatUtcTimestampFromWarsaw(date, second),
      period: '', status: 'Лид', origStatus: 'lead', revenue: 0,
    }));
  }

  rows.sort((a, b) => a._seconds - b._seconds);
  applyGeneratedFields(rows);
  rows.forEach((row) => { row.id = syntheticId(); delete row._seconds; });
  const actualRevenue = Math.round(rows.reduce((sum, row) => sum + row.revenue, 0) * 100) / 100;

  return {
    rows,
    summary: {
      total: rows.length,
      registrations: registrationCount,
      leads: rows.filter((row) => row.origStatus === 'lead').length,
      sales: rows.filter((row) => row.origStatus === 'sale').length,
      targetRevenue: target,
      actualRevenue,
      difference: Math.round((actualRevenue - target) * 100) / 100,
    },
  };
}
